import i18n from "@/utils/i18n";

export type EducationStatus =
  | "aktif"
  | "talepToplama"
  | "kapali"
  | "tamamlandi"
  | "butceBekliyor"
  | "taslak";

export type EducationFormat = "uniteli" | "canli" | "yuzYuze" | "nonUnit";

/** 0 CENTRAL (Merkez), 1 BRANCH (Taşra), 2 ALL (Tümü) */
export type TargetAudience = 0 | 1 | 2;

/** API education type: 0 ON_SITE, 1 ONLINE, 2 UNIT, 3 NONUNIT */
export const EDUCATION_TYPE_API = {
  ON_SITE: 0,
  ONLINE: 1,
  UNIT: 2,
  NONUNIT: 3,
} as const;

/** API scope type: 0 CENTRAL, 1 BRANCH, 2 ALL */
export const SCOPE_TYPE_API = {
  CENTRAL: 0,
  BRANCH: 1,
  ALL: 2,
} as const;

/** API status: 0 REQUEST_COLLECTION, 1 BUDGET_PENDING, 2 ACTIVE, 3 CLOSED, 4 ARCHIVED, 5 DRAFT */
export const EDUCATION_STATUS_API = {
  REQUEST_COLLECTION: 0,
  BUDGET_PENDING: 1,
  ACTIVE: 2,
  CLOSED: 3,
  ARCHIVED: 4,
  DRAFT: 5,
} as const;

export interface Education {
  id: string;
  educationCode: string;
  educationName: string;
  educatorName: string;
  format: EducationFormat;
  targetAudience: TargetAudience;
  registrationCount: number;
  maxRegistration?: number;
  publishDate: string;
  status: EducationStatus;
  /** Zorunlu eğitim bayrağı — listede "Zorunlu" chip'i gösterir */
  isRequired?: boolean;
  /** Geçerli kullanıcı bu eğitime per-education yönetici olarak atanmış mı?
   *  Atanmamış SUPERVISOR'a satır tıklamasında /unauthorized yerine no-op
   *  davranmak için kullanılır. */
  isCurrentUserManager?: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * GET /admin/educations/:id/mandatory/health response.
 * FE admin ekranında koşullu "Atamayı Yeniden Çalıştır" banner'ını tetiklemek
 * için kullanılır — `isHealthy=false` gelince admin aksiyon alabilir.
 */
export interface MandatoryHealthResponse {
  isHealthy: boolean;
  expectedAssignmentCount: number;
  actualAssignmentCount: number;
  missingCount: number;
  lastReconcileAt: string | null;
}

export interface CreateEducationRequest {
  educationCode: string;
  educationName: string;
  educatorName: string;
  format: EducationFormat;
  targetAudience: TargetAudience;
  maxRegistration?: number;
  publishDate: string;
  status: EducationStatus;
}

export interface UpdateEducationRequest
  extends Partial<CreateEducationRequest> {
  id: string;
}

// --- Chapters & Objectives (POST /api/admin/educations, PATCH /api/admin/educations/{id}) ---

/** Objective type enum (backend number) */
export enum ObjectiveType {
  INTERNAL_FILE = 0,
  LINK = 1,
  JITSI = 2,
}

export interface CreateObjectiveVideoQuestionOptionDto {
  optionText: string;
  order?: number;
  isCorrect: boolean;
}

export interface CreateObjectiveVideoQuestionDto {
  questionText: string;
  timestamp: string; // "HH:MM:SS"
  order?: number;
  isMandatory?: boolean;
  options: CreateObjectiveVideoQuestionOptionDto[];
  imageFileID?: string;
  allowMultipleSelection?: boolean;
}

export interface CreateObjectiveDto {
  type: ObjectiveType;
  title: string;
  fileID?: string;
  externalUrl?: string;
  order?: number;
  allowSeek?: boolean;
  isVisibleToUser?: boolean;
  videoQuestions?: CreateObjectiveVideoQuestionDto[];
}

export interface CreateEducationChapterDto {
  title: string;
  order?: number;
  durationHours?: number;
  durationMinutes?: number;
  objectives?: CreateObjectiveDto[];
}

/** POST /api/admin/educations - Backend request body */
export interface AdminCreateEducationDto {
  code?: string;
  /** true ise backend code başına geçerli yılı ekler ("ISG-101" → "2026-ISG-101"). */
  autoYearPrefix?: boolean;
  name: string;
  description?: string;
  content?: string;
  type: number; // 0 ON_SITE, 1 ONLINE, 2 UNIT, 3 NONUNIT
  scopeType: number; // 0 CENTRAL, 1 BRANCH, 2 ALL
  status?: number; // 0 REQUEST_COLLECTION, 1 BUDGET_PENDING, 2 ACTIVE, 3 CLOSED, 4 ARCHIVED
  categoryIds?: string[];
  groupId?: string;
  coverImageFileID?: string;
  documentFileIDs?: string[];
  examId?: string;
  surveyId?: string;
  certificateId?: string;
  capacity?: number;
  durationHours?: number;
  durationMinutes?: number;
  budget?: number;
  scheduledTimes?: Array<{ day: number; startTime: string; endTime: string }>;
  location?: string;
  latitude?: number;
  longitude?: number;
  presetLocationId?: string | null;
  /** Ulaşım bilgisi (metin) */
  transportation?: string;
  startDate?: string;
  endDate?: string;
  requestCollectionStartDate?: string;
  requestCollectionEndDate?: string;
  chapters?: CreateEducationChapterDto[];
  isRequired?: boolean;
  isAcceptingRequestCollection?: boolean;
  departmentIDs?: string[];
  instructorIds?: string[];
  /** Eğitim Bazlı Yönetici ID listesi (Madde 5). BE DTO alanı: educationManagerIds. */
  educationManagerIds?: string[];
  /** Bütçe gereksinimi açıkken true; bütçe onayı bekleniyor */
  isWaitingBudgedApprovement?: boolean;
}

/** PATCH /api/admin/educations/{id} - Gönderilen chapters ile mevcut bölümler tamamen değiştirilir */
export interface AdminPatchEducationDto {
  chapters?: CreateEducationChapterDto[];
  code?: string;
  name?: string;
  description?: string;
  content?: string;
  type?: number;
  scopeType?: number;
  status?: number;
  categoryIds?: string[];
  /** Kapak kaldırıldığında backend alanı temizlemek için null */
  coverImageFileID?: string | null;
  /** Dökümanlar kaldırıldığında backend listeyi temizlemek için null */
  documentFileIDs?: string[] | null;
  examId?: string;
  surveyId?: string;
  certificateId?: string;
  groupId?: string | null;
  capacity?: number;
  budget?: number;
  durationHours?: number;
  durationMinutes?: number;
  startDate?: string | null;
  endDate?: string | null;
  requestCollectionStartDate?: string | null;
  requestCollectionEndDate?: string | null;
  isAcceptingRequestCollection?: boolean;
  /** Zorunlu eğitim bayrağı. true ise backend hedef kitleye otomatik atama yapar. */
  isRequired?: boolean;
  instructorIds?: string[];
  /** Eğitim Bazlı Yönetici ID listesi (Madde 5). BE DTO alanı: educationManagerIds. */
  educationManagerIds?: string[];
  location?: string;
  latitude?: number;
  longitude?: number;
  presetLocationId?: string | null;
  /** Ulaşım bilgisi (metin) */
  transportation?: string;
  /** Bütçe onayı bekliyor mu – false yollanarak bütçe onayı kapatılır */
  isWaitingBudgedApprovement?: boolean;
  /** Bütçeyi Onayla butonu ile gönderilir; sadece onay anında true */
  isBudgetApproved?: boolean;
}

export interface EducationFilters {
  search?: string;
  /** Single or multiple (from multi-select filter) */
  status?: EducationStatus | EducationStatus[];
  format?: EducationFormat | EducationFormat[];
  targetAudience?: number | number[];
  /** GET: type (0 ON_SITE, 1 ONLINE, 2 UNIT, 3 NONUNIT) */
  type?: number | number[];
  /** GET: categoryIds */
  categoryIds?: string[];
  /** GET: instructorIds */
  instructorIds?: string[];
  /** GET: Yayınlanma tarihi aralığı – [başlangıç, bitiş] (Date veya string); API’ye startDateFrom/startDateTo olarak gider */
  publishDate?: string | [Date | string | null, Date | string | null];
  /** Zorunlu eğitim filtresi — "true" | "false" string ya da boolean */
  isRequired?: boolean | string;
}

// --- API list endpoint types (GET /api/admin/educations) ---

export interface EducationListCategoryApi {
  id: string;
  name: string;
  description: string | null;
  parentId: string | null;
  parentName: string | null;
}

export interface EducationListInstructorApi {
  id?: string;
  userID?: string;
  firstName?: string;
  lastName?: string;
}

export interface EducationListScheduledTimeApi {
  day: number;
  startTime: string;
  endTime: string;
  timezone?: string;
}

/** Tekil eğitim item (GET /api/admin/educations response data elemanı) */
export interface EducationListItemApi {
  id: string;
  code?: string | null;
  name: string;
  description: string;
  content?: string | null;
  categories: EducationListCategoryApi[];
  instructors: EducationListInstructorApi[];
  coverImageFileID: string | null;
  /** Backend'den gelen hazır kapak görseli URL'i (download yerine bunu kullan) */
  coverImageUrl?: string | null;
  type: number; // 0 ON_SITE, 1 ONLINE, 2 UNIT, 3 NONUNIT
  scopeType: number; // 0 CENTRAL, 1 BRANCH, 2 ALL
  status: number; // 0 REQUEST_COLLECTION, 1 BUDGET_PENDING, 2 ACTIVE, 3 CLOSED, 4 ARCHIVED
  isRequired: boolean;
  isAcceptingRequestCollection: boolean;
  capacity: number;
  durationHours: number;
  durationMinutes: number;
  budget: number;
  scheduledTimes: EducationListScheduledTimeApi[] | null;
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  requestCollectionStartDate: string | null;
  requestCollectionEndDate: string | null;
  exam: string | null;
  location: string | null;
  latitude: number | null;
  longitude: number | null;
  presetLocationId?: string | null;
  /** Kayıt sayısı (backend bazen döndürebilir) */
  requestedEnrollmentsCount?: number;
  /** Talep & Onay listesi için backend alanı */
  totalEnrolledCount?: number;
  /**
   * Geçerli kullanıcı bu eğitime per-education yönetici olarak atanmış mı?
   * Strict EDUCATION_SUPERVISOR / BRANCH_MANAGER için backend doldurur; diğer
   * rollerde undefined. "Plan Eğitimi" gibi manager-only aksiyonların listede
   * görünürlüğü için kullanılır.
   */
  isCurrentUserManager?: boolean;
}

/** GET /api/admin/educations response */
export interface EducationListResponseApi {
  success: boolean;
  status: number;
  data: EducationListItemApi[];
  count: number;
  message?: string;
}

// --- GET /api/admin/educations/{id} (detay) ---

export interface EducationDetailCategoryApi {
  id: string;
  name: string;
  description: string;
  parentId: string;
  parentName: string;
}

export interface EducationDetailInstructorApi {
  userID: string;
  firstName: string | null;
  lastName: string | null;
}

/** Eğitim Bazlı Yönetici — eğitim için düzenleme yetkisine sahip kullanıcı */
export interface EducationDetailManagerApi {
  userID: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

export interface EducationDetailScheduledTimeApi {
  day: number;
  startTime: string;
  endTime: string;
  timezone: string;
}

/** Backend'den dönen bölüm/hedef (GET detay) */
export interface EducationDetailChapterApi {
  id?: string;
  title: string;
  order?: number;
  durationHours?: number;
  durationMinutes?: number;
  objectives?: Array<{
    id?: string;
    title?: string;
    fileID?: string;
    /** 2 = JITSI */
    type?: number;
    order?: number;
    /** Jitsi/Meet URL - type 2 veya meet.jit.si içeriyorsa kullan */
    externalUrl?: string;
    allowSeek?: boolean;
    educationVideoId?: string;
    educationVideoName?: string;
    videoQuestions?: Array<{
      questionText?: string;
      timestamp?: string;
      order?: number;
      imageFileID?: string;
      allowMultipleSelection?: boolean;
      isMandatory?: boolean;
      options?: Array<{
        id?: string;
        optionText?: string;
        order?: number;
        isCorrect?: boolean;
      }>;
    }>;
  }>;
}

/** Eğitim detayındaki enrollment status kırılımı (onay detay ekranı için). */
export interface EnrollmentBreakdownApi {
  pending: number;
  enrolled: number;
  rejected: number;
  cancelled: number;
  total: number;
}

/** GET /api/admin/educations/{id} response data */
export interface EducationDetailApi {
  id: string;
  code?: string | null;
  name: string;
  description: string;
  content?: string | null;
  requestedEnrollmentsCount: number;
  /** BE yeni alan: enrollment kayıtlarının status bazlı dağılımı. */
  enrollmentBreakdown?: EnrollmentBreakdownApi;
  /** Onaylı + devam eden kayıt sayısı (breakdown.enrolled ile aynı). */
  totalEnrolledCount?: number;
  categories: EducationDetailCategoryApi[];
  instructors: EducationDetailInstructorApi[];
  /** Eğitim Bazlı Yöneticiler — BE yeni spec (17.04.2026 Madde 5).
   *  Seçilen kişiler o eğitim için düzenleme yetkisine sahip olur. */
  managers?: EducationDetailManagerApi[];
  departmentID: string;
  coverImageFileID: string;
  /** Backend'den gelen hazır kapak görseli URL'i (download yerine bunu kullan) */
  coverImageUrl?: string | null;
  type: number;
  scopeType: number;
  status: number;
  isRequired: boolean;
  isAcceptingRequestCollection: boolean;
  capacity: number;
  durationHours: number;
  durationMinutes: number;
  budget: number;
  scheduledTimes: EducationDetailScheduledTimeApi[];
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  requestCollectionStartDate: string;
  requestCollectionEndDate: string;
  /** Backend bazen { id, passingScore, name } objesi veya string id döner */
  exam?: string | { id: string; passingScore?: number; name?: string };
  /** Anket ID – string veya { id: string } */
  survey?: string | { id: string };
  /** Sertifika ID (modaldan seçilen) */
  certificateId?: string | null;
  groupId?: string | null;
  groupName?: string | null;
  group?: { id: string; name?: string | null } | null;
  location: string;
  latitude: number;
  longitude: number;
  presetLocationId?: string | null;
  /** Ulaşım bilgisi (metin) */
  transportation?: string | null;
  /** Backend döndürüyorsa bölümler ve hedefler */
  chapters?: EducationDetailChapterApi[];
  documentFileIDs?: string[];
  /** Backend'den gelen döküman listesi (id, originalName, downloadUrl) */
  documentFiles?: Array<{
    id: string;
    originalName?: string;
    downloadUrl?: string;
  }>;
  /** Bütçe onayı bekliyor mu */
  isWaitingBudgedApprovement?: boolean;
  /** Bütçe onaylandı mı */
  isBudgetApproved?: boolean;
}

export interface PresetLocationApi {
  id: string;
  name: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PresetLocationsResponseApi {
  success: boolean;
  status: number;
  data: PresetLocationApi[];
  message?: string;
  count?: number;
}

/** GET /api/admin/educations/{id} response wrapper */
export interface EducationDetailResponseApi {
  success: boolean;
  status: number;
  data: EducationDetailApi;
  message?: string;
}

export interface EducationListParams {
  page?: number;
  limit?: number;
  search?: string;
  type?: number;
  scopeType?: number;
  status?: number;
  /** Birden çok status filtresi (ör. "Onaylandı" = [ACTIVE, CLOSED, ARCHIVED]). */
  statusIn?: number[];
  categoryIds?: string[];
  instructorIds?: string[];
  /** Yayınlanma Tarihi aralığı – backend’e startDateFrom / startDateTo olarak gönderilir */
  startDateFrom?: string;
  startDateTo?: string;
  isRequired?: boolean;
  isAcceptingRequestCollection?: boolean;
  sortBy?:
    | "publishedAt"
    | "requestedEnrollmentsCount"
    | "createdAt"
    | "duration"
    | "requestCollectionEndDate";
  sortOrder?: "asc" | "desc";
}

// --- GET /api/educations/{educationId}/attendance (Instructor only) ---

/** 0 = Katılmadı, 1 = Katıldı, null = Girilmedi */
export type AttendanceStatusApi = 0 | 1 | null;

/** Mazeret Nedeni — 17.04.2026 toplantı, Madde 10.
 *  BE ile birebir eşleşen sabit int enum. status=0 (Katılmadı) ise ZORUNLU. */
export enum ExcuseReason {
  HEALTH = 0,
  ANNUAL_LEAVE = 1,
  BUSINESS_TRAVEL = 2,
  OFFICIAL_DUTY = 3,
  FAMILY_EMERGENCY = 4,
  OTHER = 5,
}

export const EXCUSE_REASON_LABELS: Record<ExcuseReason, string> = {
  [ExcuseReason.HEALTH]: i18n.t("excuseReasons.health"),
  [ExcuseReason.ANNUAL_LEAVE]: i18n.t("excuseReasons.annualLeave"),
  [ExcuseReason.BUSINESS_TRAVEL]: i18n.t("excuseReasons.businessTravel"),
  [ExcuseReason.OFFICIAL_DUTY]: i18n.t("excuseReasons.officialDuty"),
  [ExcuseReason.FAMILY_EMERGENCY]: "Ailevi Mazeret",
  [ExcuseReason.OTHER]: i18n.t("excuseReasons.other"),
};

export interface AttendanceItemApi {
  enrollmentId: string;
  userId: string;
  personnelId: string;
  personnelName: string;
  title: string;
  department: string;
  attendanceDate: string;
  status: AttendanceStatusApi;
  statusText?: string;
  /** Madde 10: status = 0 (Katılmadı) ise dolu, aksi durumlarda null. */
  excuseReason?: ExcuseReason | null;
}

export interface AttendanceListResponseApi {
  attendanceDate: string;
  items: AttendanceItemApi[];
  total: number;
  page: number;
  limit: number;
}

export interface GetAttendanceParams {
  attendanceDate: string; // YYYY-MM-DD
  page?: number;
  limit?: number;
  search?: string;
  /** 0 = NOT_ATTENDED, 1 = ATTENDED */
  attendanceStatus?: 0 | 1;
  /** Sanal sınıf filtresi — sadece bu sınıfın katılımcıları döner */
  virtualClassroomId?: string;
}

// --- PUT /api/educations/{educationId}/attendance (Instructor only, idempotent) ---

export interface PutAttendanceEntry {
  enrollmentId: string;
  /** 0 = Katılmadı, 1 = Katıldı */
  attendanceStatus: 0 | 1;
  /** Madde 10: attendanceStatus === 0 ise ZORUNLU, === 1 ise gönderme. */
  excuseReason?: ExcuseReason;
}

export interface PutAttendanceBody {
  attendanceDate: string; // YYYY-MM-DD
  attendances: PutAttendanceEntry[];
}

// --- PATCH /api/admin/educations/education-enrollments/:enrollmentId/attendance (tekil) ---

export interface PatchAttendanceBody {
  attendanceDate: string; // YYYY-MM-DD
  enrollmentId: string;
  /** 0 = Katılmadı, 1 = Katıldı */
  attendanceStatus: 0 | 1;
  /** Madde 10: attendanceStatus === 0 ise ZORUNLU. */
  excuseReason?: ExcuseReason;
}
